/**
 * proposeDigitNames — which branch is probably which digit (ADR-022 §6, §8).
 *
 * ONE responsibility: given the analyzer's branches and a thumb anchor, ORDER
 * and NAME them. It places nothing.
 *
 * WHY THIS IS A PROPOSAL AND NO LONGER A PLACEMENT PATH. Until 2026-08-11 this
 * logic lived inside `placeDigits` as the "inferred path" — the branch to take
 * when no human had said which digit was which. ADR-022 makes the human's pins
 * the only placement input, so inference stops being an alternative source of
 * truth and becomes what it was always better at: a PRE-FILL a person confirms
 * or corrects (Allen's D4 ruling — "as long as pre-fill can be re-assigned, we
 * should do it"). The ratified anatomy survives the demotion intact, because it
 * is good anatomy and it makes the pre-fill right more often:
 *
 * **The ulnar-most finger is ALWAYS the Pinky** (Allen, 2026-07-15, on the
 * 4-digit kobold): a creature with three fingers and a thumb is missing its
 * RING, not its pinky. This is not cosmetic — the name picks which Mixamo
 * channel drives the digit at retarget, so the outer finger inherits the
 * distinctive outer curl rather than ring motion.
 *
 * Returns a PROPOSAL with margins, never a decision: `confident` is false when
 * two candidates sit too close along the knuckle-row axis to separate honestly
 * (ADR-010 §11's rule for the thumb pre-fill, applied to the ordering), so a
 * surface can offer a name without asserting it. An unresolved thumb yields an
 * EMPTY proposal and a named marker — a hand nobody has anchored cannot be
 * ordered, and guessing would be exactly the silent-wrong the engine forbids.
 *
 * TWO CALLERS, ONE ORDERING RULE. The Document path names BRANCHES (ordered by
 * their bases, the knuckle row proper); the pin surface names PLACES a person
 * may click, which are TIPS and may have no branch at all. Both go through
 * {@link nameAroundThumb}, so "the knuckle-row axis" cannot come to mean two
 * things — the single definition that ADR-022 §7 gate 9 asks for, applied to
 * the ordering as well as to the grammar and the space conversion.
 *
 * Depends on: hands/separation-profile (branch shape), hands/voxel-math.
 * Depended on by: the app's hand surface (pre-fill) and run-document's
 * pin construction; NEVER by placement, which takes named tips only.
 */
import type { BranchChain, EvidenceMarker } from './separation-profile.js';
import { centroidOf, dist, dominantAxis, dot, sub } from './voxel-math.js';
import type { VoxelPoint } from './voxel-grid.js';

export type DigitName = 'Thumb' | 'Index' | 'Middle' | 'Ring' | 'Pinky';

/** The ordered finger names, thumb-adjacent first. */
const FINGER_ORDER: readonly DigitName[] = ['Index', 'Middle', 'Ring', 'Pinky'];
/** Every digit name, anatomical order — the assignment vocabulary and its sort key. */
export const DIGIT_ORDER: readonly DigitName[] = ['Thumb', ...FINGER_ORDER];

/**
 * Ordering margin below which two neighbouring fingers are NOT separable along
 * the knuckle-row axis, as a fraction of the row's own extent. Under it the
 * proposal is offered but flagged unconfident — a surface should show the names
 * as suggestions rather than pre-committing a person to them.
 */
const ORDER_MARGIN_FRACTION = 0.08;

export interface DigitNameProposal {
  /** branch id → the digit it probably is. Empty when nothing can be anchored. */
  readonly assignment: Readonly<Record<string, DigitName>>;
  /** False when any adjacent pair was too close along the row axis to separate. */
  readonly confident: boolean;
  readonly markers: readonly EvidenceMarker[];
}

export interface ProposeDigitNamesOptions {
  /** The user-pinned thumb branch id — the one human bit this inference needs. */
  readonly thumbBranchId?: string;
  /** The analyzer's own §4 thumb pre-fill, used only when no pin is given. */
  readonly autoThumbBranchId?: string | null;
}

/**
 * A namable thing at a place: a branch (ordered by its base) or a candidate tip
 * (ordered by the tip itself). The space is the CALLER'S — voxel-index for the
 * Document path, bind-world for the pin surface — and never mixed within one
 * call, because every operation here is a comparison between two of these.
 */
export interface NameablePoint {
  readonly id: string;
  readonly at: VoxelPoint;
}

/**
 * Name an ordered (thumb-adjacent → ulnar-most) finger set of `count` branches.
 * Anchors: Index holds the radial end, Pinky the ulnar end, Middle→Ring fill
 * inward and drop innermost-first. A LONE finger takes Index — a single
 * opposing digit reads as the index — and any set below four is flagged by the
 * caller's `incomplete-digit-set` marker.
 */
export function nameFingerSet(count: number): readonly DigitName[] {
  if (count <= 1) return ['Index'];
  if (count >= FINGER_ORDER.length) return FINGER_ORDER;
  return [...FINGER_ORDER.slice(0, count - 1), 'Pinky'];
}

export function proposeDigitNames(
  branches: readonly BranchChain[],
  options: ProposeDigitNamesOptions = {},
): DigitNameProposal {
  const markers: EvidenceMarker[] = [];
  const thumbId = options.thumbBranchId ?? options.autoThumbBranchId ?? null;

  if (options.thumbBranchId !== undefined && !branches.some((b) => b.id === options.thumbBranchId)) {
    // Loud, but not fatal: a proposal is advice, and advice must not crash a
    // surface. The pin named a branch this analysis does not have (it renumbered
    // under the slider) — say so and propose nothing.
    return {
      assignment: {},
      confident: false,
      markers: [{ code: 'thumb-pin-unmatched', detail: { branchId: options.thumbBranchId } }],
    };
  }

  const thumb = thumbId !== null ? branches.find((b) => b.id === thumbId) ?? null : null;
  if (thumb === null) {
    markers.push({
      code: 'thumb-unresolved',
      detail: { note: 'no pin and no auto-thumb — nothing to anchor an ordering against', branches: branches.length },
    });
    return { assignment: {}, confident: false, markers };
  }

  const fingers = branches.filter((b) => b.id !== thumb.id);
  const named = nameAroundThumb(
    { id: thumb.id, at: thumb.chain[0]! },
    fingers.map((f) => ({ id: f.id, at: f.chain[0]! })),
  );
  return { ...named, markers: [...markers, ...named.markers] };
}

/**
 * Name candidate TIPS around a thumb tip — the pin surface's pre-fill (D4).
 *
 * Same rule, different evidence. A person clicks fingertips, so the places this
 * orders are tips rather than knuckle bases; the row they lie along is the same
 * row, read at the far end. `thumbId` must name one of `tips`, and naming it is
 * the caller's problem: on the panel it comes from the analyzer's own §4
 * margin-guarded thumb pre-fill, which returns nothing rather than guess — and
 * when it returns nothing, this proposes nothing and the person names their own
 * pins, which is the honest outcome and not a failure.
 */
export function proposeNamesForTips(
  tips: readonly NameablePoint[],
  options: { readonly thumbId: string },
): DigitNameProposal {
  const thumb = tips.find((t) => t.id === options.thumbId);
  if (thumb === undefined) {
    return {
      assignment: {},
      confident: false,
      markers: [{ code: 'thumb-pin-unmatched', detail: { branchId: options.thumbId } }],
    };
  }
  return nameAroundThumb(thumb, tips.filter((t) => t.id !== thumb.id));
}

/**
 * THE ORDERING RULE, once. Order the non-thumb points along their dominant
 * (knuckle-row) axis, ORIENT that axis by 3D proximity of the two ends to the
 * thumb — robust to the eigenvector's arbitrary sign — and name the result
 * thumb-adjacent → ulnar-most.
 *
 * ADR-022 §8 states the orientation as `sign(side)·(armAxis × palmNormal)`.
 * That form is NOT what runs here, and the difference is worth stating rather
 * than papering over: the palm normal this codebase computes
 * (`authorHandFrames`) is sign-resolved by a `dorsalHint` — a CONVENTION,
 * defaulting to palms-down +Y, and its own `palm-normal-sign` marker says a
 * true palmar/dorsal cue is still owed. Orienting by it would therefore smuggle
 * the palms-down assumption back in under a formula that exists to remove it,
 * and would be wrong in exactly the supinated cases §8 names (fox-kigu, the
 * palms-front HD intake). The thumb anchor is sign-free and measured, so it is
 * what the ordering uses; the cross-product form becomes available the day a
 * palmar cue is measured, and §8.1 of the ADR records that debt.
 */
function nameAroundThumb(thumb: NameablePoint, fingers: readonly NameablePoint[]): DigitNameProposal {
  const markers: EvidenceMarker[] = [];
  if (fingers.length > FINGER_ORDER.length) {
    // Not a refusal any more (ADR-022): the person simply will not pin the
    // extras. Name the four that order best is NOT offered either — with six
    // candidates we cannot know which four, so we propose the thumb alone.
    markers.push({ code: 'anomalous-branch-count', detail: { nonThumbBranches: fingers.length } });
    return { assignment: { [thumb.id]: 'Thumb' }, confident: false, markers };
  }
  if (fingers.length < FINGER_ORDER.length) {
    markers.push({ code: 'incomplete-digit-set', detail: { fingerCount: fingers.length } });
  }

  const assignment: Record<string, DigitName> = { [thumb.id]: 'Thumb' };
  if (fingers.length === 0) return { assignment, confident: true, markers };

  const places = fingers.map((f) => f.at);
  const mean = centroidOf(places);
  const axis = dominantAxis(places);
  const withT = fingers.map((f) => ({ f, t: dot(sub(f.at, mean), axis) }));
  withT.sort((a, b) => a.t - b.t);
  const ordered =
    dist(withT[withT.length - 1]!.f.at, thumb.at) < dist(withT[0]!.f.at, thumb.at) ? [...withT].reverse() : withT;

  // Margin check: adjacent fingers must separate along the row by more than a
  // small fraction of the row's own extent, or the ordering is a coin-flip.
  const extent = Math.abs(ordered[ordered.length - 1]!.t - ordered[0]!.t);
  let confident = true;
  if (ordered.length > 1 && !(extent > 1e-9)) {
    // A ROW WITH NO EXTENT IS NOT A CONFIDENT ROW (Codex P2, #90). Every place
    // projects to the same point on the axis, so the geometry offers no
    // ordering at all — and the sort that follows would fall through to array
    // order, which is an artifact of how the branches were enumerated. Guarding
    // only the per-gap ratio (its denominator being zero) would let that
    // arbitrary order out of here wearing `confident: true`.
    confident = false;
    markers.push({ code: 'digit-order-ambiguous', detail: { reason: 'row-has-no-extent', fingers: ordered.length } });
  } else {
    for (let i = 1; i < ordered.length; i += 1) {
      const gap = Math.abs(ordered[i]!.t - ordered[i - 1]!.t);
      if (extent > 1e-9 && gap / extent < ORDER_MARGIN_FRACTION) {
        confident = false;
        markers.push({
          code: 'digit-order-ambiguous',
          detail: { a: ordered[i - 1]!.f.id, b: ordered[i]!.f.id, gapFraction: Number((gap / extent).toFixed(4)) },
        });
      }
    }
  }

  const names = nameFingerSet(ordered.length);
  ordered.forEach((entry, i) => {
    assignment[entry.f.id] = names[i]!;
  });
  return { assignment, confident, markers };
}

/** The tip of a branch chain — the placement-relevant point (ADR-022 §2). */
export function branchTip(branch: BranchChain): VoxelPoint {
  return branch.chain[branch.chain.length - 1]!;
}
